import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import '../css/notification.css';
import { NotificationContextType, NotificationOptions, Notification } from '../interfaces';

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification debe usarse dentro de NotificationProvider');
  }
  return context;
};

// Hook compatible con notistack
export const useSnackbar = () => {
  const { enqueueNotification, closeNotification } = useNotification();
  
  return {
    enqueueSnackbar: enqueueNotification,
    closeSnackbar: closeNotification,
  };
};

// ========================================
// PROVIDER PRINCIPAL
// ========================================

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const duplicateCheckRef = useRef<Map<string, number>>(new Map());

  const generateId = useCallback(() => {
    return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const getMessageKey = useCallback((message: React.ReactNode): string => {
    if (typeof message === 'string') return message;
    if (React.isValidElement(message)) {
      return JSON.stringify(message.props) || 'react-element';
    }
    return JSON.stringify(message);
  }, []);

  const closeNotification = useCallback((id: string) => {
    // Limpiar timeout si existe
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }

    // Remover con animación
    setNotifications(prev => {
      const notification = prev.find(n => n.id === id);
      if (notification?.options.onClose) {
        notification.options.onClose();
      }
      return prev.map(n => n.id === id ? { ...n, removing: true } : n);
    });

    // Remover completamente después de la animación
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 300);
  }, []);

  const enqueueNotification = useCallback((
    message: React.ReactNode, 
    options: NotificationOptions = {}
  ): string => {
    const {
      variant = 'info',
      autoHideDuration = 4000,
      persist = false,
      preventDuplicate = true,
      position = 'bottom-left',
      onClose
    } = options;

    // Verificar duplicados
    if (preventDuplicate) {
      const messageKey = getMessageKey(message);
      const lastTime = duplicateCheckRef.current.get(messageKey);
      const now = Date.now();
      
      if (lastTime && (now - lastTime) < 1000) {
        return '';
      }
      
      duplicateCheckRef.current.set(messageKey, now);
    }

    const id = generateId();
    const notification: Notification = {
      id,
      message,
      options: { ...options, variant, autoHideDuration, persist, position },
      timestamp: Date.now()
    };

    setNotifications(prev => [...prev, notification]);

    // Configurar auto-hide si no es persistente
    if (!persist && autoHideDuration > 0) {
      const timeout = setTimeout(() => {
        closeNotification(id);
      }, autoHideDuration);
      
      timeoutsRef.current.set(id, timeout);
    }

    return id;
  }, [generateId, getMessageKey, closeNotification]);

  const clearAll = useCallback(() => {
    timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutsRef.current.clear();
    duplicateCheckRef.current.clear();
    setNotifications([]);
  }, []);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      timeoutsRef.current.clear();
    };
  }, []);

  const contextValue: NotificationContextType = {
    notifications,
    enqueueNotification,
    closeNotification,
    clearAll
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <NotificationContainer notifications={notifications} onClose={closeNotification} />
    </NotificationContext.Provider>
  );
};

// ========================================
// CONTAINER DE NOTIFICACIONES
// ========================================

const NotificationContainer: React.FC<{
  notifications: Notification[];
  onClose: (id: string) => void;
}> = ({ notifications, onClose }) => {
  // Agrupar por posición
  const groupedNotifications = notifications.reduce((groups, notification) => {
    const position = notification.options.position || 'bottom-left';
    if (!groups[position]) {
      groups[position] = [];
    }
    groups[position].push(notification);
    return groups;
  }, {} as Record<string, Notification[]>);

  return (
    <>
      {Object.entries(groupedNotifications).map(([position, positionNotifications]) => (
        <div key={position} className={`notification-container ${position}`}>
          {positionNotifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onClose={onClose}
            />
          ))}
        </div>
      ))}
    </>
  );
};

// ========================================
// ITEM INDIVIDUAL
// ========================================

const NotificationItem: React.FC<{
  notification: Notification;
  onClose: (id: string) => void;
}> = ({ notification, onClose }) => {
  const { id, message, options } = notification;
  const { variant = 'info', autoHideDuration = 4000, persist, action } = options;

  const getIcon = (variant: string) => {
    switch (variant) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'info': return 'ℹ️';
      default: return 'ℹ️';
    }
  };

  const showProgress = !persist && autoHideDuration > 0;

  return (
    <div 
      className={`notification-item ${variant} ${notification.removing ? 'removing' : ''}`}
      role="alert"
      aria-live="polite"
    >
      <div className="notification-content">
        <span className="notification-icon">
          {getIcon(variant)}
        </span>
        <div className="notification-message">
          {message}
        </div>
      </div>
      
      {action && (
        <div className="notification-actions">
          {action}
        </div>
      )}
      
      <button
        className="notification-close"
        onClick={() => onClose(id)}
        aria-label="Cerrar notificación"
        title="Cerrar"
      >
        ×
      </button>
      
      {showProgress && (
        <div 
          className="notification-progress"
          style={{
            animationDuration: `${autoHideDuration}ms`
          }}
        />
      )}
    </div>
  );
};